import { apiClient } from './api.client';
import { AxiosError } from 'axios';

/**
 * API endpoint status enumeration
 */
export enum EndpointStatus {
  IMPLEMENTED = 'implemented',
  IN_PROGRESS = 'in_progress', 
  NEEDED = 'needed',
  CHECKING = 'checking',
  ERROR = 'error'
}

/**
 * API endpoint information interface
 */
export interface ApiEndpoint {
  /** Endpoint path */
  path: string;
  /** HTTP method */
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Current implementation status */
  status: EndpointStatus;
  /** Whether authentication is required */
  requiresAuth: boolean;
  /** Frontend usage description */
  usage: string;
  /** Implementation priority */
  priority: 'critical' | 'high' | 'medium' | 'low';
  /** Additional notes */
  notes?: string;
  /** Last checked timestamp */
  lastChecked?: Date;
  /** Response time in milliseconds */
  responseTime?: number;
  /** Error details if check failed */
  error?: string;
}

/**
 * API health dashboard data interface
 */
export interface ApiHealthData {
  /** Overall API health status */
  overallStatus: 'healthy' | 'degraded' | 'down';
  /** Total number of endpoints by status */
  statusCounts: Record<EndpointStatus, number>;
  /** Percentage of implemented endpoints */
  implementationProgress: number;
  /** Critical endpoints that are blocking */
  blockingEndpoints: ApiEndpoint[];
  /** Recently checked endpoints */
  recentChecks: ApiEndpoint[];
  /** Feature flags based on endpoint availability */
  featureFlags: Record<string, boolean>;
  /** Last full health check timestamp */
  lastHealthCheck: Date;
  /** Average response time for working endpoints */
  averageResponseTime: number;
}

/**
 * Backend team requirements export data
 */
export interface BackendRequirements {
  /** Critical missing endpoints blocking features */
  criticalMissing: ApiEndpoint[];
  /** High priority endpoints affecting UX */
  highPriorityMissing: ApiEndpoint[];
  /** All missing endpoints by priority */
  missingByPriority: Record<string, ApiEndpoint[]>;
  /** Implementation timeline suggestions */
  implementationOrder: string[];
  /** Frontend impact assessment */
  frontendImpact: string[];
  /** Export timestamp */
  exportedAt: Date;
  /** Frontend version */
  frontendVersion: string;
}

/**
 * Known API endpoints with their current expected status
 * This serves as the source of truth for frontend-backend coordination
 */
const KNOWN_ENDPOINTS: ApiEndpoint[] = [
  // Core Infrastructure
  {
    path: '/health',
    method: 'GET',
    status: EndpointStatus.IMPLEMENTED,
    requiresAuth: false,
    usage: 'System monitoring, health checks',
    priority: 'high',
    notes: 'Public endpoint, no auth required'
  },
  {
    path: '/api/v1/auth/test',
    method: 'GET', 
    status: EndpointStatus.IMPLEMENTED,
    requiresAuth: true,
    usage: 'Authentication validation',
    priority: 'high',
    notes: 'Used in auth service'
  },
  {
    path: '/api/v1/auth/admin-test',
    method: 'GET',
    status: EndpointStatus.IMPLEMENTED,
    requiresAuth: true,
    usage: 'Admin role verification',
    priority: 'high',
    notes: 'Admin dashboard initialization'
  },

  // Authentication & Authorization (CRITICAL MISSING)
  {
    path: '/api/v1/auth/login',
    method: 'POST',
    status: EndpointStatus.NEEDED,
    requiresAuth: false,
    usage: 'User login flow',
    priority: 'critical',
    notes: 'BLOCKING: Required for JWT auth'
  },
  {
    path: '/api/v1/auth/logout',
    method: 'POST',
    status: EndpointStatus.NEEDED,
    requiresAuth: true,
    usage: 'User logout flow',
    priority: 'high',
    notes: 'Token invalidation'
  },
  {
    path: '/api/v1/auth/refresh',
    method: 'POST',
    status: EndpointStatus.NEEDED,
    requiresAuth: false,
    usage: 'Token refresh',
    priority: 'critical',
    notes: 'BLOCKING: Auto-refresh functionality'
  },
  {
    path: '/api/v1/auth/me',
    method: 'GET',
    status: EndpointStatus.NEEDED,
    requiresAuth: true,
    usage: 'User profile data',
    priority: 'high',
    notes: 'Dashboard user info'
  },

  // Client Management
  {
    path: '/api/v1/clients',
    method: 'GET',
    status: EndpointStatus.IMPLEMENTED,
    requiresAuth: true,
    usage: 'Admin client list, dashboard',
    priority: 'high',
    notes: 'Supports filtering by status'
  },
  {
    path: '/api/v1/clients/:id',
    method: 'GET', 
    status: EndpointStatus.IMPLEMENTED,
    requiresAuth: true,
    usage: 'Client detail pages, statistics',
    priority: 'high',
    notes: 'Includes conversation stats'
  },
  {
    path: '/api/v1/clients',
    method: 'POST',
    status: EndpointStatus.IMPLEMENTED,
    requiresAuth: true,
    usage: 'Admin client creation',
    priority: 'medium',
    notes: 'Admin-only functionality'
  },
  {
    path: '/api/v1/clients/:id',
    method: 'PUT',
    status: EndpointStatus.IMPLEMENTED,
    requiresAuth: true,
    usage: 'Admin client editing',
    priority: 'medium',
    notes: 'Partial updates supported'
  },
  {
    path: '/api/v1/clients/:id',
    method: 'DELETE',
    status: EndpointStatus.NEEDED,
    requiresAuth: true,
    usage: 'Admin client removal',
    priority: 'low',
    notes: 'Soft delete recommended'
  },

  // Lead Management  
  {
    path: '/api/v1/clients/:id/leads',
    method: 'GET',
    status: EndpointStatus.IMPLEMENTED,
    requiresAuth: true,
    usage: 'Lead listings, analytics',
    priority: 'high',
    notes: 'Pagination & filtering working'
  },
  {
    path: '/api/v1/leads/:id',
    method: 'GET',
    status: EndpointStatus.NEEDED,
    requiresAuth: true,
    usage: 'Lead detail view',
    priority: 'medium',
    notes: 'Individual lead information'
  },
  {
    path: '/api/v1/leads/:id',
    method: 'PUT',
    status: EndpointStatus.NEEDED,
    requiresAuth: true,
    usage: 'Lead status updates',
    priority: 'medium',
    notes: 'Status management'
  },

  // Conversation & Analytics (CRITICAL MISSING)
  {
    path: '/api/v1/clients/:id/conversations',
    method: 'GET',
    status: EndpointStatus.NEEDED,
    requiresAuth: true,
    usage: 'Conversation history',
    priority: 'critical',
    notes: 'BLOCKING: Core client feature'
  },
  {
    path: '/api/v1/conversations/:id',
    method: 'GET',
    status: EndpointStatus.NEEDED,
    requiresAuth: true,
    usage: 'Conversation details',
    priority: 'high',
    notes: 'Chat transcripts, recordings'
  },
  {
    path: '/api/v1/clients/:id/analytics',
    method: 'GET',
    status: EndpointStatus.NEEDED,
    requiresAuth: true,
    usage: 'Dashboard metrics',
    priority: 'critical',
    notes: 'BLOCKING: Statistics widgets'
  },

  // User Management
  {
    path: '/api/v1/users',
    method: 'GET',
    status: EndpointStatus.IMPLEMENTED,
    requiresAuth: true,
    usage: 'Admin user management',
    priority: 'medium',
    notes: 'Admin dashboard'
  },
  {
    path: '/api/v1/users',
    method: 'POST',
    status: EndpointStatus.NEEDED,
    requiresAuth: true,
    usage: 'User creation',
    priority: 'low',
    notes: 'Admin functionality'
  },

  // Bot Configuration (Developer Features)
  {
    path: '/api/v1/clients/:id/bots',
    method: 'GET',
    status: EndpointStatus.NEEDED,
    requiresAuth: true,
    usage: 'Bot management dashboard',
    priority: 'high',
    notes: 'Developer settings'
  },
  {
    path: '/api/v1/clients/:id/bots',
    method: 'POST',
    status: EndpointStatus.NEEDED,
    requiresAuth: true,
    usage: 'Bot creation',
    priority: 'medium',
    notes: 'Bot deployment'
  },

  // Webhook Management
  {
    path: '/webhooks/vapi',
    method: 'POST',
    status: EndpointStatus.IMPLEMENTED,
    requiresAuth: false,
    usage: 'Real-time data ingestion',
    priority: 'high',
    notes: 'VAPI integration working'
  }
];

/**
 * API Status Service Class
 * Provides runtime endpoint availability checking and health monitoring
 */
class ApiStatusService {
  private endpointCache = new Map<string, ApiEndpoint>();
  private lastHealthCheck?: Date;
  private healthCheckInterval?: NodeJS.Timeout;

  constructor() {
    this.initializeEndpointCache();
  }

  /**
   * Initialize the endpoint cache with known endpoints
   */
  private initializeEndpointCache(): void {
    KNOWN_ENDPOINTS.forEach(endpoint => {
      const key = this.getEndpointKey(endpoint.method, endpoint.path);
      this.endpointCache.set(key, { ...endpoint });
    });
  }

  /**
   * Generate a unique key for endpoint caching
   */
  private getEndpointKey(method: string, path: string): string {
    return `${method}:${path}`;
  }

  /**
   * Check if a specific endpoint is available
   * @param endpoint - Endpoint path (e.g., '/api/v1/users')
   * @param method - HTTP method (default: 'GET')
   * @returns Promise<boolean> - Whether the endpoint is available
   */
  async checkEndpointAvailability(endpoint: string, method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'GET'): Promise<boolean> {
    const key = this.getEndpointKey(method, endpoint);
    const endpointInfo = this.endpointCache.get(key);
    
    if (!endpointInfo) {
      // Unknown endpoint, try to check it anyway
      return this.performEndpointCheck(endpoint, method);
    }

    // If we know it's not implemented, return false immediately
    if (endpointInfo.status === EndpointStatus.NEEDED) {
      return false;
    }

    // If it was recently checked and working, return true
    if (endpointInfo.status === EndpointStatus.IMPLEMENTED && 
        endpointInfo.lastChecked && 
        Date.now() - endpointInfo.lastChecked.getTime() < 5 * 60 * 1000) { // 5 minutes cache
      return true;
    }

    // Perform live check
    return this.performEndpointCheck(endpoint, method, endpointInfo);
  }

  /**
   * Perform actual HTTP check for endpoint availability
   */
  private async performEndpointCheck(
    endpoint: string, 
    method: string, 
    endpointInfo?: ApiEndpoint
  ): Promise<boolean> {
    const startTime = Date.now();
    const ENDPOINT_CHECK_TIMEOUT = 5000;
    const NON_CRITICAL_STATUS_VALIDATOR = (status: number) => status < 500;
    
    try {
      // Update status to checking
      if (endpointInfo) {
        endpointInfo.status = EndpointStatus.CHECKING;
        endpointInfo.lastChecked = new Date();
      }

      // Perform appropriate HTTP method check
      await this.executeHttpCheck(method, endpoint, ENDPOINT_CHECK_TIMEOUT, NON_CRITICAL_STATUS_VALIDATOR);

      const responseTime = Date.now() - startTime;

      // Update endpoint info if available
      if (endpointInfo) {
        endpointInfo.status = EndpointStatus.IMPLEMENTED;
        endpointInfo.responseTime = responseTime;
        endpointInfo.error = undefined;
      }

      return true;
    } catch (error) {
      const responseTime = Date.now() - startTime;
      
      // Update endpoint info if available
      if (endpointInfo) {
        this.updateEndpointError(endpointInfo, error as AxiosError, responseTime);
      }

      return false;
    }
  }

  /**
   * Execute HTTP check for specific method
   */
  private async executeHttpCheck(
    method: string, 
    endpoint: string, 
    timeout: number, 
    validateStatus: (status: number) => boolean
  ): Promise<void> {
    const config = { timeout, validateStatus };
    
    switch (method) {
      case 'GET':
        await apiClient.get(endpoint, config);
        break;
      case 'POST':
        await apiClient.post(endpoint, {}, config);
        break;
      case 'PUT':
        await apiClient.put(endpoint, {}, config);
        break;
      case 'PATCH':
        await apiClient.patch(endpoint, {}, config);
        break;
      case 'DELETE':
        await apiClient.delete(endpoint, config);
        break;
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }
  }

  /**
   * Update endpoint info when an error occurs
   */
  private updateEndpointError(endpointInfo: ApiEndpoint, error: AxiosError, responseTime: number): void {
    // Check if it's a 404 (not implemented) vs other errors
    if (error.response?.status === 404) {
      endpointInfo.status = EndpointStatus.NEEDED;
    } else {
      endpointInfo.status = EndpointStatus.ERROR;
    }
    endpointInfo.responseTime = responseTime;
    endpointInfo.error = error.message;
  }

  /**
   * Get the current status of a specific endpoint
   * @param endpoint - Endpoint path
   * @param method - HTTP method (default: 'GET')
   * @returns EndpointStatus information
   */
  async getEndpointStatus(endpoint: string, method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'GET'): Promise<ApiEndpoint | null> {
    const key = this.getEndpointKey(method, endpoint);
    const endpointInfo = this.endpointCache.get(key);
    
    if (!endpointInfo) {
      return null;
    }

    // Refresh status if needed
    await this.checkEndpointAvailability(endpoint, method);
    
    return { ...endpointInfo };
  }

  /**
   * Generate feature flags based on endpoint availability
   * @returns Record<string, boolean> - Feature flags
   */
  generateFeatureFlags(): Record<string, boolean> {
    const flags: Record<string, boolean> = {};
    
    // Core authentication features
    flags.hasLogin = this.isEndpointImplemented('POST', '/api/v1/auth/login');
    flags.hasTokenRefresh = this.isEndpointImplemented('POST', '/api/v1/auth/refresh');
    flags.hasUserProfile = this.isEndpointImplemented('GET', '/api/v1/auth/me');
    
    // Client features
    flags.hasClientManagement = this.isEndpointImplemented('GET', '/api/v1/clients');
    flags.hasClientCreation = this.isEndpointImplemented('POST', '/api/v1/clients');
    flags.hasClientDetails = this.isEndpointImplemented('GET', '/api/v1/clients/:id');
    
    // Lead features
    flags.hasLeadListing = this.isEndpointImplemented('GET', '/api/v1/clients/:id/leads');
    flags.hasLeadDetails = this.isEndpointImplemented('GET', '/api/v1/leads/:id');
    flags.hasLeadUpdates = this.isEndpointImplemented('PUT', '/api/v1/leads/:id');
    
    // Conversation features (critical)
    flags.hasConversations = this.isEndpointImplemented('GET', '/api/v1/clients/:id/conversations');
    flags.hasConversationDetails = this.isEndpointImplemented('GET', '/api/v1/conversations/:id');
    flags.hasAnalytics = this.isEndpointImplemented('GET', '/api/v1/clients/:id/analytics');
    
    // Bot management features
    flags.hasBotManagement = this.isEndpointImplemented('GET', '/api/v1/clients/:id/bots');
    flags.hasBotCreation = this.isEndpointImplemented('POST', '/api/v1/clients/:id/bots');
    
    // Admin features
    flags.hasUserManagement = this.isEndpointImplemented('GET', '/api/v1/users');
    flags.hasUserCreation = this.isEndpointImplemented('POST', '/api/v1/users');
    
    // Webhook features
    flags.hasWebhooks = this.isEndpointImplemented('POST', '/webhooks/vapi');
    
    return flags;
  }

  /**
   * Check if an endpoint is implemented based on cached status
   */
  private isEndpointImplemented(method: string, path: string): boolean {
    const key = this.getEndpointKey(method, path);
    const endpoint = this.endpointCache.get(key);
    return endpoint?.status === EndpointStatus.IMPLEMENTED;
  }

  /**
   * Get comprehensive API health data for dashboard
   * @returns Promise<ApiHealthData> - Health dashboard data
   */
  async getAPIHealthData(): Promise<ApiHealthData> {
    // Perform health checks for critical endpoints
    await this.performCriticalHealthChecks();
    
    const endpoints = Array.from(this.endpointCache.values());
    
    // Calculate status counts
    const statusCounts = endpoints.reduce((counts, endpoint) => {
      counts[endpoint.status] = (counts[endpoint.status] || 0) + 1;
      return counts;
    }, {} as Record<EndpointStatus, number>);

    // Ensure all status types are represented
    Object.values(EndpointStatus).forEach(status => {
      if (!statusCounts[status]) {
        statusCounts[status] = 0;
      }
    });

    // Calculate implementation progress
    const implementedCount = statusCounts[EndpointStatus.IMPLEMENTED] || 0;
    const totalCount = endpoints.length;
    const implementationProgress = Math.round((implementedCount / totalCount) * 100);

    // Find blocking endpoints (critical priority that are not implemented)
    const blockingEndpoints = endpoints.filter(endpoint => 
      endpoint.priority === 'critical' && 
      endpoint.status !== EndpointStatus.IMPLEMENTED
    );

    // Get recently checked endpoints (last 5 minutes)
    const recentChecks = endpoints
      .filter(endpoint => 
        endpoint.lastChecked && 
        Date.now() - endpoint.lastChecked.getTime() < 5 * 60 * 1000
      )
      .sort((a, b) => (b.lastChecked?.getTime() || 0) - (a.lastChecked?.getTime() || 0))
      .slice(0, 10);

    // Calculate overall status
    let overallStatus: 'healthy' | 'degraded' | 'down';
    if (blockingEndpoints.length > 0) {
      overallStatus = 'down';
    } else if (implementationProgress < 80) {
      overallStatus = 'degraded';
    } else {
      overallStatus = 'healthy';
    }

    // Calculate average response time
    const workingEndpoints = endpoints.filter(endpoint => 
      endpoint.status === EndpointStatus.IMPLEMENTED && endpoint.responseTime
    );
    const averageResponseTime = workingEndpoints.length > 0
      ? Math.round(workingEndpoints.reduce((sum, endpoint) => sum + (endpoint.responseTime || 0), 0) / workingEndpoints.length)
      : 0;

    this.lastHealthCheck = new Date();

    return {
      overallStatus,
      statusCounts,
      implementationProgress,
      blockingEndpoints,
      recentChecks,
      featureFlags: this.generateFeatureFlags(),
      lastHealthCheck: this.lastHealthCheck,
      averageResponseTime
    };
  }

  /**
   * Perform health checks for critical endpoints only (faster)
   */
  private async performCriticalHealthChecks(): Promise<void> {
    const criticalEndpoints = Array.from(this.endpointCache.values())
      .filter(endpoint => endpoint.priority === 'critical');

    // Check critical endpoints in parallel with a timeout
    const promises = criticalEndpoints.map(endpoint => 
      this.checkEndpointAvailability(endpoint.path, endpoint.method)
        .catch(() => false) // Ignore errors, just return false
    );

    await Promise.allSettled(promises);
  }

  /**
   * Get all known endpoints with their current status
   * @returns ApiEndpoint[] - Array of all known endpoints
   */
  getAllEndpoints(): ApiEndpoint[] {
    return Array.from(this.endpointCache.values());
  }

  /**
   * Get missing endpoints organized by priority
   * @returns Record<string, ApiEndpoint[]> - Missing endpoints by priority
   */
  getMissingEndpointsByPriority(): Record<string, ApiEndpoint[]> {
    const endpoints = Array.from(this.endpointCache.values());
    const missing = endpoints.filter(endpoint => endpoint.status === EndpointStatus.NEEDED);
    
    return missing.reduce((groups, endpoint) => {
      const priority = endpoint.priority;
      if (!groups[priority]) {
        groups[priority] = [];
      }
      groups[priority].push(endpoint);
      return groups;
    }, {} as Record<string, ApiEndpoint[]>);
  }

  /**
   * Generate backend team requirements export
   * @returns BackendRequirements - Structured requirements for backend team
   */
  generateBackendRequirements(): BackendRequirements {
    const endpoints = Array.from(this.endpointCache.values());
    const missingEndpoints = endpoints.filter(endpoint => endpoint.status === EndpointStatus.NEEDED);
    
    const criticalMissing = missingEndpoints.filter(ep => ep.priority === 'critical');
    const highPriorityMissing = missingEndpoints.filter(ep => ep.priority === 'high');
    
    const missingByPriority = this.getMissingEndpointsByPriority();
    
    // Suggested implementation order based on frontend impact
    const implementationOrder = [
      'Authentication endpoints (login, refresh, me) - Critical for user flow',
      'Conversation and analytics endpoints - Core client features',
      'Lead detail management - Enhanced user experience', 
      'Bot management for developers - Developer tools',
      'Admin utilities and webhook monitoring - Administrative features'
    ];

    // Frontend impact assessment
    const frontendImpact = [
      `${criticalMissing.length} critical endpoints blocking core functionality`,
      `${highPriorityMissing.length} high-priority endpoints affecting user experience`,
      'Mock data fallback currently active for missing features',
      'User authentication flow requires immediate implementation',
      'Dashboard statistics unavailable without analytics endpoints'
    ];

    return {
      criticalMissing,
      highPriorityMissing,
      missingByPriority,
      implementationOrder,
      frontendImpact,
      exportedAt: new Date(),
      frontendVersion: '1.3.5'
    };
  }

  /**
   * Start periodic health monitoring
   * @param intervalMinutes - Check interval in minutes (default: 5)
   */
  startHealthMonitoring(intervalMinutes: number = 5): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }

    this.healthCheckInterval = setInterval(async () => {
      try {
        await this.performCriticalHealthChecks();
      } catch (error) {
        console.error('[API Status] Health check failed:', error);
      }
    }, intervalMinutes * 60 * 1000);
  }

  /**
   * Stop periodic health monitoring
   */
  stopHealthMonitoring(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = undefined;
    }
  }

  /**
   * Get endpoints filtered by status
   * @param status - Filter by specific status
   * @returns ApiEndpoint[] - Filtered endpoints
   */
  getEndpointsByStatus(status: EndpointStatus): ApiEndpoint[] {
    return Array.from(this.endpointCache.values())
      .filter(endpoint => endpoint.status === status);
  }
}

// Export singleton instance
export const apiStatusService = new ApiStatusService();

// Export class for custom instances
export { ApiStatusService };

// Export utility functions
export const checkEndpointAvailability = (endpoint: string, method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE') => 
  apiStatusService.checkEndpointAvailability(endpoint, method);

export const getEndpointStatus = (endpoint: string, method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE') => 
  apiStatusService.getEndpointStatus(endpoint, method);

export const generateFeatureFlags = () => 
  apiStatusService.generateFeatureFlags();

export const getAPIHealthData = () => 
  apiStatusService.getAPIHealthData();